import React, { useState, useEffect, useMemo } from 'react';
import { Car, User, Navigation, Plus, X, Search, Trash2, Calendar, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../api.js';
import SimpleTable from '../common/SimpleTable.jsx';
import { isFlagOn } from '../../utils/dbFlags';

// ============================================================================
// SIMPLIFIED ASSIGNMENTS VIEW
// Features: pagination, sorting, inline editing, minimal modals
// ============================================================================

// Status badge component
function StatusBadge({ active, activeKey = 'active' }) {
  const { t } = useTranslation();
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
      active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
    }`}>
      {active ? t(`assignments.status.${activeKey}`) : t('assignments.status.ended')}
    </span>
  );
}

// Type badge for location assignments
function TypeBadge({ type }) {
  const { t } = useTranslation();
  const styles = {
    permanent: 'bg-blue-100 text-blue-800',
    temporary: 'bg-yellow-100 text-yellow-800',
    maintenance: 'bg-orange-100 text-orange-800',
    standby: 'bg-gray-100 text-gray-600'
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type] || styles.standby}`}>
      {type ? t(`assignments.type.${type}`, { defaultValue: type }) : ''}
    </span>
  );
}

// Unified Assignment Modal - handles both create and edit for driver/location
function AssignmentModal({ isOpen, onClose, onSave, type, assignment, vehicles, drivers, sites }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (assignment) {
      setForm({ ...assignment });
    } else {
      setForm({
        vehicle_id: '',
        driver_id: '',
        site_id: '',
        start_date: new Date().toISOString().split('T')[0],
        assignment_type: 'permanent',
        purpose: '',
        notes: ''
      });
    }
  }, [assignment, isOpen]);

  if (!isOpen) return null;

  const isEdit = !!assignment;
  const isDriver = type === 'driver';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form, isEdit);
      onClose();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEnd = async () => {
    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      if (isDriver) {
        await api.trip.update(assignment.id, { end_date: today, status: 'completed' });
      } else {
        await api.vehicleLocation.update(assignment.id, { is_current: 0, removed_at: today });
      }
      onClose();
      window.location.reload(); // Simple refresh
    } catch (err) {
      console.error('End error:', err);
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit
    ? (isDriver ? t('assignments.form.titleEditDriver') : t('assignments.form.titleEditSite'))
    : (isDriver ? t('assignments.form.titleCreateDriver') : t('assignments.form.titleCreateSite'));

  const colorClass = isDriver ? 'blue' : 'green';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className={`bg-${colorClass}-500 text-white px-6 py-4 flex justify-between items-center rounded-t-lg`}
             style={{ backgroundColor: isDriver ? '#3b82f6' : '#22c55e' }}>
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Vehicle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Car className="w-4 h-4 inline mr-1" /> {t('assignments.form.vehicle')}
            </label>
            <select
              value={form.vehicle_id || ''}
              onChange={(e) => setForm({ ...form, vehicle_id: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('common.actions.select')}</option>
              {vehicles.map(v => (
                <option key={v.id} value={v.id}>
                  {v.make} {v.model} ({v.license_plate})
                </option>
              ))}
            </select>
          </div>

          {/* Driver (for driver assignments) */}
          {isDriver && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="w-4 h-4 inline mr-1" /> {t('assignments.form.driver')}
              </label>
              <select
                value={form.driver_id || ''}
                onChange={(e) => setForm({ ...form, driver_id: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">{t('common.actions.select')}</option>
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.first_name} {d.last_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Site (for location assignments) */}
          {!isDriver && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Navigation className="w-4 h-4 inline mr-1" /> {t('assignments.form.site')}
              </label>
              <select
                value={form.site_id || ''}
                onChange={(e) => setForm({ ...form, site_id: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                required
              >
                <option value="">{t('common.actions.select')}</option>
                {sites.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.site_name} ({s.city})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" /> {t('assignments.form.startDate')}
            </label>
            <input
              type="date"
              value={form.start_date || form.assigned_at || ''}
              onChange={(e) => setForm({ ...form, start_date: e.target.value, assigned_at: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Type (for location) */}
          {!isDriver && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assignments.form.type')}</label>
              <select
                value={form.assignment_type || 'permanent'}
                onChange={(e) => setForm({ ...form, assignment_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="permanent">{t('assignments.type.permanent')}</option>
                <option value="temporary">{t('assignments.type.temporary')}</option>
                <option value="maintenance">{t('assignments.type.maintenance')}</option>
                <option value="standby">{t('assignments.type.standby')}</option>
              </select>
            </div>
          )}

          {/* Purpose/Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isDriver ? t('assignments.form.purpose') : t('assignments.form.notes')}
            </label>
            <input
              type="text"
              value={form.purpose || form.notes || ''}
              onChange={(e) => setForm({ ...form, purpose: e.target.value, notes: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={isDriver ? t('assignments.form.purposePlaceholder') : t('assignments.form.notesPlaceholder')}
            />
          </div>

          {/* End button for active assignments */}
          {isEdit && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <button
                type="button"
                onClick={handleEnd}
                disabled={saving}
                className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
              >
                {t('assignments.form.endAssignment')}
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              style={{ backgroundColor: isDriver ? '#3b82f6' : '#22c55e' }}
            >
              {saving ? t('common.states.saving') : t('common.actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete Confirmation Modal
function DeleteModal({ isOpen, onClose, onConfirm, info, deleting }) {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-lg font-bold">{t('assignments.delete.title')}</h3>
        </div>
        <p className="text-gray-600 mb-6">{t('assignments.delete.message', { info })}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={deleting} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            {t('common.actions.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
          >
            {deleting ? t('assignments.delete.deleting') : t('common.actions.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function AssignmentsView({ onMessage }) {
  const { t } = useTranslation();

  // Data state
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [sites, setSites] = useState([]);
  const [driverAssignments, setDriverAssignments] = useState([]);
  const [locationAssignments, setLocationAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState('drivers');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [modalType, setModalType] = useState(null); // 'driver' or 'location'
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load data
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vehiclesRes, driversRes, sitesRes, tripsRes, locationsRes] = await Promise.all([
        api.vehicle.getAll(),
        api.driver.getAll(),
        api.workingSite.getAll(),
        api.trip.getAll(),
        api.vehicleLocation.getAll(),
      ]);

      setVehicles(vehiclesRes.data || []);
      setDrivers(driversRes.data || []);
      setSites(sitesRes.data || []);
      setDriverAssignments(tripsRes.data || []);
      setLocationAssignments(locationsRes.data || []);
    } catch (error) {
      console.error('Load error:', error);
      onMessage?.({ type: 'error', text: t('common.states.loadError') });
    } finally {
      setLoading(false);
    }
  };

  // Helpers
  const getVehicle = (id) => vehicles.find(v => v.id === id);
  const getDriver = (id) => drivers.find(d => d.id === id);
  const getSite = (id) => sites.find(s => s.id === id);

  // Filter and transform data for tables
  const filteredDriverAssignments = useMemo(() => {
    return driverAssignments
      .map(a => {
        const vehicle = getVehicle(a.vehicle_id);
        const driver = getDriver(a.driver_id);
        const isActive = !a.end_date && a.status !== 'completed';
        return {
          ...a,
          vehicleLabel: vehicle ? `${vehicle.make} ${vehicle.model}` : '-',
          licensePlate: vehicle?.license_plate || '',
          driverLabel: driver ? `${driver.first_name} ${driver.last_name}` : '-',
          isActive,
          _inactive: !isActive
        };
      })
      .filter(a => showInactive || a.isActive)
      .filter(a => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return a.vehicleLabel.toLowerCase().includes(term) ||
               a.licensePlate.toLowerCase().includes(term) ||
               a.driverLabel.toLowerCase().includes(term);
      });
  }, [driverAssignments, vehicles, drivers, showInactive, searchTerm]);

  const filteredLocationAssignments = useMemo(() => {
    return locationAssignments
      .map(a => {
        const vehicle = getVehicle(a.vehicle_id);
        const site = getSite(a.site_id);
        const isActive = isFlagOn(a.is_current);
        return {
          ...a,
          vehicleLabel: vehicle ? `${vehicle.make} ${vehicle.model}` : '-',
          licensePlate: vehicle?.license_plate || '',
          siteLabel: site ? `${site.site_name} (${site.city})` : '-',
          isActive,
          _inactive: !isActive
        };
      })
      .filter(a => showInactive || a.isActive)
      .filter(a => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return a.vehicleLabel.toLowerCase().includes(term) ||
               a.licensePlate.toLowerCase().includes(term) ||
               a.siteLabel.toLowerCase().includes(term);
      });
  }, [locationAssignments, vehicles, sites, showInactive, searchTerm]);

  // Table columns
  const driverColumns = [
    { key: 'vehicleLabel', label: t('assignments.columns.vehicle'), render: (v, row) => <span className="font-medium">{v} <span className="text-gray-500">({row.licensePlate})</span></span> },
    { key: 'driverLabel', label: t('assignments.columns.driver') },
    { key: 'start_date', label: t('assignments.columns.startDate'), render: v => v || '-' },
    { key: 'end_date', label: t('assignments.columns.endDate'), render: v => v || t('assignments.columns.ongoing') },
    { key: 'isActive', label: t('assignments.columns.status'), render: v => <StatusBadge active={v} /> },
    { key: 'actions', label: '', sortable: false, render: (_, row) => (
      <div className="flex gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); setEditingAssignment(row); setModalType('driver'); }}
          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
        >
          {t('common.actions.edit')}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'driver', id: row.id, info: `${row.driverLabel} → ${row.vehicleLabel}` }); }}
          className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    )}
  ];

  const locationColumns = [
    { key: 'vehicleLabel', label: t('assignments.columns.vehicle'), render: (v, row) => <span className="font-medium">{v} <span className="text-gray-500">({row.licensePlate})</span></span> },
    { key: 'siteLabel', label: t('assignments.columns.site') },
    { key: 'assignment_type', label: t('assignments.columns.type'), render: v => <TypeBadge type={v} /> },
    { key: 'assigned_at', label: t('assignments.columns.since'), render: v => v || '-' },
    { key: 'isActive', label: t('assignments.columns.status'), render: v => <StatusBadge active={v} activeKey="current" /> },
    { key: 'actions', label: '', sortable: false, render: (_, row) => (
      <div className="flex gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); setEditingAssignment(row); setModalType('location'); }}
          className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
        >
          {t('common.actions.edit')}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'location', id: row.id, info: `${row.vehicleLabel} → ${row.siteLabel}` }); }}
          className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    )}
  ];

  // Save handler
  const handleSave = async (form, isEdit) => {
    try {
      if (modalType === 'driver') {
        const payload = {
          vehicle_id: form.vehicle_id,
          driver_id: form.driver_id,
          start_date: form.start_date,
          purpose: form.purpose,
          status: 'in_progress'
        };
        if (isEdit) {
          await api.trip.update(form.id, payload);
        } else {
          await api.trip.create(payload);
        }
      } else {
        const payload = {
          vehicle_id: form.vehicle_id,
          site_id: form.site_id,
          assignment_type: form.assignment_type,
          assigned_at: form.assigned_at || form.start_date,
          notes: form.notes,
          is_current: 1
        };
        if (isEdit) {
          await api.vehicleLocation.update(form.id, payload);
        } else {
          await api.vehicleLocation.create(payload);
        }
      }
      onMessage?.({ type: 'success', text: t('assignments.messages.saved') });
      loadData();
    } catch (err) {
      onMessage?.({ type: 'error', text: t('assignments.errors.generic', { message: err.message }) });
      throw err;
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'driver') {
        await api.trip.delete(deleteTarget.id);
      } else {
        await api.vehicleLocation.delete(deleteTarget.id);
      }
      onMessage?.({ type: 'success', text: t('assignments.messages.deleted') });
      loadData();
    } catch (err) {
      onMessage?.({ type: 'error', text: t('assignments.errors.generic', { message: err.message }) });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  // Close modal
  const closeModal = () => {
    setModalType(null);
    setEditingAssignment(null);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-600">{t('common.states.loading')}</div>;
  }

  return (
    <div className="flex gap-6 h-full">
      {/* Sidebar */}
      <div className="w-56 flex-shrink-0">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-500 mb-3">{t('assignments.sidebar.title')}</h3>
          <nav className="space-y-1">
            <button
              onClick={() => setActiveTab('drivers')}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === 'drivers' ? 'bg-amber-100 text-amber-800' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('assignments.tabs.drivers')}
              <span className="float-right text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                {filteredDriverAssignments.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('locations')}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === 'locations' ? 'bg-amber-100 text-amber-800' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t('assignments.tabs.sites')}
              <span className="float-right text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                {filteredLocationAssignments.length}
              </span>
            </button>
          </nav>

          {/* Filter toggle */}
          <div className="mt-6 pt-4 border-t">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-gray-300"
              />
              {t('assignments.list.showEnded')}
            </label>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">
            {activeTab === 'drivers' ? t('assignments.list.titleDrivers') : t('assignments.list.titleSites')}
          </h1>
          <button
            onClick={() => { setModalType(activeTab === 'drivers' ? 'driver' : 'location'); setEditingAssignment(null); }}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('assignments.list.newAssignment')}
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('assignments.list.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {activeTab === 'drivers' ? (
            <SimpleTable
              columns={driverColumns}
              data={filteredDriverAssignments}
              pageSize={10}
              emptyMessage={showInactive ? t('assignments.list.emptyDrivers') : t('assignments.list.emptyActive')}
              rowClassName={(row) => row._inactive ? 'bg-gray-50 opacity-60' : ''}
            />
          ) : (
            <SimpleTable
              columns={locationColumns}
              data={filteredLocationAssignments}
              pageSize={10}
              emptyMessage={showInactive ? t('assignments.list.emptySites') : t('assignments.list.emptyActive')}
              rowClassName={(row) => row._inactive ? 'bg-gray-50 opacity-60' : ''}
            />
          )}
        </div>
      </div>

      {/* Assignment Modal */}
      <AssignmentModal
        isOpen={!!modalType}
        onClose={closeModal}
        onSave={handleSave}
        type={modalType}
        assignment={editingAssignment}
        vehicles={vehicles}
        drivers={drivers}
        sites={sites}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        info={deleteTarget?.info}
        deleting={deleting}
      />
    </div>
  );
}

export default AssignmentsView;
